'use client';

import React, { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const floatingCards = [
{
  id: 'mercedes',
  label: 'Mercedes Benz',
  sublabel: 'Genuine Parts',
  color: 'from-slate-700 to-slate-900',
  accent: '#C41E3A',
  icon: '🔧',
  animClass: 'animate-float-slow',
  position: 'top-16 left-4 md:top-20 md:left-8',
  delay: '0s'
},
{
  id: 'scania',
  label: 'Scania',
  sublabel: 'Engine & Drivetrain',
  color: 'from-brand-blue to-brand-blue-dark',
  accent: '#F5A623',
  icon: '⚙️',
  animClass: 'animate-float-medium',
  position: 'top-32 right-4 md:top-24 md:right-10',
  delay: '1s'
},
{
  id: 'volvo',
  label: 'Volvo',
  sublabel: 'Brake Systems',
  color: 'from-brand-red to-brand-red-dark',
  accent: '#FFFFFF',
  icon: '🛞',
  animClass: 'animate-float-fast',
  position: 'bottom-32 left-6 md:bottom-28 md:left-12',
  delay: '0.5s'
},
{
  id: 'man',
  label: 'MAN Trucks',
  sublabel: 'Suspension & Filters',
  color: 'from-yellow-600 to-yellow-800',
  accent: '#FFFFFF',
  icon: '🔩',
  animClass: 'animate-float-slow',
  position: 'bottom-20 right-6 md:bottom-24 md:right-14',
  delay: '1.5s'
}];


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const moveX = (clientX - innerWidth / 2) / innerWidth;
      const moveY = (clientY - innerHeight / 2) / innerHeight;

      const glowRed = heroRef.current.querySelector('.hero-glow-red') as HTMLElement;
      const glowBlue = heroRef.current.querySelector('.hero-glow-blue') as HTMLElement;

      if (glowRed) {
        glowRed.style.transform = `translate(${moveX * 40}px, ${moveY * 40}px)`;
      }
      if (glowBlue) {
        glowBlue.style.transform = `translate(${moveX * -30}px, ${moveY * -30}px)`;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      id="hero"
      className="hero-section relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ minHeight: '100vh' }}>
      
      {/* Grid background */}
      <div className="hero-grid-pattern" aria-hidden="true" />

      {/* Atmospheric glows */}
      <div
        className="hero-glow-red"
        style={{ top: '15%', left: '-5%', transition: 'transform 0.8s cubic-bezier(0.25,0.46,0.45,0.94)' }}
        aria-hidden="true" />
      
      <div
        className="hero-glow-blue"
        style={{ bottom: '10%', right: '-5%', transition: 'transform 0.8s cubic-bezier(0.25,0.46,0.45,0.94)' }}
        aria-hidden="true" />
      
      <div
        className="hero-glow-yellow"
        style={{ top: '40%', right: '25%' }}
        aria-hidden="true" />
      

      {/* Floating Brand Cards — hidden on mobile to avoid overlap */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        {floatingCards.map((card) =>
        <div
          key={card.id}
          className={`absolute ${card.position} ${card.animClass} z-10 hidden md:block`}
          style={{ animationDelay: card.delay }}>
          
            <div className={`glass-dark rounded-2xl px-4 py-3 min-w-[140px] shadow-2xl`}>
              <div className="flex items-center gap-2">
                <span className="text-xl">{card.icon}</span>
                <div>
                  <p className="text-white font-heading font-bold text-sm leading-tight">{card.label}</p>
                  <p className="text-white/50 text-xs font-body">{card.sublabel}</p>
                </div>
              </div>
              <div
              className="mt-2 h-0.5 rounded-full"
              style={{ background: card.accent, opacity: 0.6 }} />
            
            </div>
          </div>
        )}
      </div>

      {/* Hero truck image - background */}
      <div className="absolute inset-0 opacity-20" aria-hidden="true">
        <AppImage
          src="https://img.rocket.new/generatedImages/rocket_gen_img_14769bf9d-1766519489204.png"
          alt="Heavy duty truck on East African highway representing Baninvest spare parts coverage"
          fill
          className="object-cover object-center"
          priority />
        
        <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/60 via-transparent to-brand-dark/80" />
      </div>

      {/* Main Content */}
      <div className="relative z-20 max-w-6xl mx-auto px-5 sm:px-6 text-center pt-24 pb-16 sm:pt-0 sm:pb-0">
        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full glass-dark mb-6 sm:mb-8 border border-brand-yellow/20"
          style={{ animation: 'slide-in-up 0.8s ease forwards' }}>
          
          <span className="w-2 h-2 rounded-full bg-brand-yellow animate-pulse flex-shrink-0" />
          <span className="text-brand-yellow text-xs font-heading font-bold uppercase tracking-widest">
            East Africa&apos;s Leading Truck Parts Supplier
          </span>
        </div>

        {/* Headline */}
        <h1
          className="font-heading font-black text-white mb-5 sm:mb-6 leading-none tracking-tight hero-headline"
          style={{
            fontSize: 'clamp(36px, 7vw, 100px)',
            lineHeight: 1.05,
            animation: 'slide-in-up 0.9s ease 0.1s both'
          }}>
          
          Premium Truck{' '}
          <span className="shimmer-text">Spare Parts</span>
          <br />
          <span className="text-gradient-red-blue">for Every Brand</span>
        </h1>

        {/* Subheadline */}
        <p
          className="text-white/65 font-body max-w-2xl mx-auto mb-8 sm:mb-10 hero-sub leading-relaxed"
          style={{
            fontSize: 'clamp(14px, 2vw, 20px)',
            animation: 'slide-in-up 1s ease 0.2s both'
          }}>
          
          Mercedes Benz · MAN · Scania · Volvo · BPW · HOWO · Beiben &amp; all major European brands.
          Serving Uganda and the entire East African region from Kampala.
        </p>

        {/* CTA Buttons */}
        <div
          className="flex flex-col xs:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16"
          style={{ animation: 'slide-in-up 1s ease 0.3s both' }}>
          
          <a href="https://wa.me/256772500220" target="_blank" rel="noopener noreferrer" className="btn-primary w-full xs:w-auto">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            WhatsApp Us Now
          </a>
          <a href="#categories" className="btn-secondary w-full xs:w-auto" style={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }}>
            <Icon name="Squares2X2Icon" size={18} className="text-white" />
            Browse Parts
          </a>
        </div>

        {/* Trust Badges */}
        <div
          className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 md:gap-10"
          style={{ animation: 'slide-in-up 1s ease 0.4s both' }}>
          
          {[
          { label: '8+ Brands Covered', icon: 'StarIcon' },
          { label: '10,000+ Parts Available', icon: 'CubeIcon' },
          { label: 'East Africa Delivery', icon: 'TruckIcon' },
          { label: 'Kampala, Uganda', icon: 'MapPinIcon' }].
          map((badge) =>
          <div key={badge.label} className="flex items-center gap-2">
              <Icon name={badge.icon as 'StarIcon'} size={16} className="text-brand-yellow" variant="solid" />
              <span className="text-white/60 text-xs sm:text-sm font-body font-medium">{badge.label}</span>
            </div>
          )}
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-20">
        <span className="text-white/30 text-xs font-heading font-bold uppercase tracking-widest">Scroll</span>
        <div className="w-px h-10 bg-white/10 relative overflow-hidden">
          <div
            className="absolute top-0 left-0 w-full h-1/2 bg-brand-yellow"
            style={{ animation: 'float-fast 1.5s ease-in-out infinite' }} />
          
        </div>
      </div>
    </section>);

}